import { memo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle2, UserX } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency, formatDocument } from "@/shared/lib/formatters";
import { CNPJ_LENGTH, CPF_LENGTH, cleanDocument } from "@/shared/lib/document";
import { CommercialInvoiceEmitResult } from "./CommercialInvoiceEmitResult";
import { useBuyerClientLookup } from "../hooks/useBuyerClientLookup";
import { NFCE_UNIDENTIFIED_VALUE_LIMIT } from "../constants";
import type {
  CommercialInvoiceRequestDTO,
  CommercialInvoiceResponseDTO,
} from "../types/commercial-invoice.types";

const SELECT_CLASSES =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm";

const schema = z.object({
  value: z
    .number({ error: "Valor obrigatório" })
    .positive("Valor deve ser positivo"),
  paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
  buyerDocument: z
    .string()
    .refine(
      (v) => v === "" || v.length === CPF_LENGTH || v.length === CNPJ_LENGTH,
      "Informe um CPF (11 dígitos) ou CNPJ (14 caracteres)",
    ),
  email: z.string().email("E-mail inválido").or(z.literal("")),
});

type FormData = z.infer<typeof schema>;

const DEFAULT_VALUES: FormData = {
  value: Number.NaN,
  paymentMethod: "PIX",
  buyerDocument: "",
  email: "",
};

interface CommercialInvoiceEmitDialogProps {
  open: boolean;
  loading: boolean;
  result: CommercialInvoiceResponseDTO | null;
  rejection: string | null;
  onClose: () => void;
  onSubmit: (data: CommercialInvoiceRequestDTO) => void;
}

export const CommercialInvoiceEmitDialog = memo(
  function CommercialInvoiceEmitDialog({
    open,
    loading,
    result,
    rejection,
    onClose,
    onSubmit,
  }: CommercialInvoiceEmitDialogProps) {
    const form = useForm<FormData>({
      resolver: zodResolver(schema),
      defaultValues: DEFAULT_VALUES,
    });

    const buyerDocument = form.watch("buyerDocument");
    const value = form.watch("value");
    const lookup = useBuyerClientLookup(buyerDocument);

    const isDocumentComplete =
      buyerDocument.length === CPF_LENGTH ||
      buyerDocument.length === CNPJ_LENGTH;
    const exceedsUnidentifiedLimit =
      !isDocumentComplete &&
      Number.isFinite(value) &&
      value > NFCE_UNIDENTIFIED_VALUE_LIMIT;

    const handleSubmit = (data: FormData) => {
      onSubmit({
        value: data.value,
        paymentMethod: data.paymentMethod,
        buyerDocument: data.buyerDocument || undefined,
        email: data.email || undefined,
      });
    };

    return (
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {result ? "Nota comercial emitida" : "Emitir nota comercial"}
            </DialogTitle>
            <DialogDescription>
              {result
                ? "Guarde a chave de acesso ou abra o DANFE."
                : "NFC-e avulsa, com item único. Só o valor e a forma de pagamento são obrigatórios."}
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <>
              <CommercialInvoiceEmitResult invoice={result} />
              <DialogFooter className="mt-6 border-t pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Concluir
                </Button>
              </DialogFooter>
            </>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor (R$) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            placeholder="0,00"
                            autoFocus
                            value={Number.isNaN(field.value) ? "" : field.value}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? Number.NaN
                                  : Number(e.target.value),
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Forma de pagamento *</FormLabel>
                        <FormControl>
                          <select
                            value={field.value}
                            onChange={field.onChange}
                            className={SELECT_CLASSES}
                          >
                            <option value="PIX">PIX</option>
                            <option value="CREDIT_CARD">Crédito</option>
                            <option value="DEBIT_CARD">Débito</option>
                            <option value="CASH">Dinheiro</option>
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="buyerDocument"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF/CNPJ do comprador</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Opcional"
                          value={formatDocument(field.value)}
                          onChange={(e) =>
                            field.onChange(
                              cleanDocument(e.target.value).slice(
                                0,
                                CNPJ_LENGTH,
                              ),
                            )
                          }
                        />
                      </FormControl>
                      {lookup.searching && (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Spinner className="w-3 h-3" />
                          Procurando cliente cadastrado…
                        </p>
                      )}
                      {lookup.client && (
                        <p className="flex items-center gap-2 text-xs text-emerald-500">
                          <CheckCircle2 className="w-3 h-3 shrink-0" />
                          {lookup.client.name}
                        </p>
                      )}
                      {lookup.notFound && (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground">
                          <UserX className="w-3 h-3 shrink-0" />
                          Sem cliente cadastrado com este documento — a nota sai
                          assim mesmo, sem criar cadastro.
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail do comprador</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Opcional — a nota é enviada por e-mail"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {rejection && (
                  <div className="flex items-start gap-3 rounded-md border border-destructive/20 bg-destructive/10 p-4">
                    <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        A SEFAZ recusou a nota
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {rejection}
                      </p>
                    </div>
                  </div>
                )}

                {exceedsUnidentifiedLimit && (
                  <div className="flex items-start gap-3 rounded-md border border-amber-500/20 bg-amber-500/10 p-4">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Acima de {formatCurrency(NFCE_UNIDENTIFIED_VALUE_LIMIT)} a
                      SEFAZ costuma exigir a identificação do comprador. Informe
                      o CPF/CNPJ ou divida em notas menores — a emissão continua
                      liberada, mas pode ser rejeitada.
                    </p>
                  </div>
                )}

                <DialogFooter className="mt-6 border-t pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading} className="gap-2">
                    {loading && <Spinner className="w-4 h-4" />}
                    Emitir NFC-e
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);
